import { useCallback, useRef } from 'react';
import { CallResult, EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { InboxTag } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { SMSDataV4, SMSDataSharedUtils } from '@frontend/api-sms-data';
import { VoicemailApi } from '@frontend/api-voicemails';
import { getUser } from '@frontend/auth-helpers';
import { emitCallTagsExternalSync, emitOpenVoicemailInsightPanel } from '@frontend/inbox-call-item-event-bus';
import { GlobalPanel } from '@frontend/panel-engine';

const DEFAULT_DELAY = 3500;

type UseVoiceTagMutationArgs = {
  channelId: string;
  threadId?: string;
  callResult: CallResult;
  threadCallId: string;
  startTime: string;
  entityCreatedAt: string;
  locationId?: string;
  entityType?: EntityType;
  onApplySuccess: (tag: Tag) => void;
  onApplyError: (error: any, tag: Tag) => void;
  onDismissOptimisticSuccess: (alertActionOnClick: () => void, autoDismissAfter: number) => void;
  onDismissError: () => void;
  dismissDelay?: number;
};

type MutationContext = {
  addTagRevertOptimisticUpdate?: () => void;
  dismissTagRevertOptimisticUpdate?: () => void;
  addTagDidOptimisticUpdate?: boolean;
  dismissTagDidOptimisticUpdate?: boolean;
  updatedThreadItemTagIds: string[];
};

type tagMutationOptimisticFnArgs = { tag: Tag };

/**
 * Custom hook for managing voice thread item tag operations with unified thread cache integration.
 * Provides optimistic updates for both applying and dismissing tags on call records.
 *
 * @param args - Configuration object for the hook
 * @param args.channelId - The unique identifier for the call record
 * @param args.threadCallId - The unique identifier for the thread call to distinguish between voicemails and callRecords
 * @param args.threadId - Optional thread identifier for unified thread updates
 * @param args.startTime - The start time of the call record
 * @param args.entityCreatedAt - The creation timestamp of the tagged entity (call record or voicemail)
 * @param args.locationId - Optional location identifier for the call record
 * @param args.onApplySuccess - Callback fired when tag application succeeds
 * @param args.onApplyError - Callback fired when tag application fails
 * @param args.onDismissOptimisticSuccess - Callback fired when tag dismissal optimistic update succeeds
 * @param args.onDismissError - Callback fired when tag dismissal fails
 * @param args.dismissDelay - Delay in milliseconds before executing tag dismissal (default: 3500)
 * @returns Object containing applyTag and dismissTag functions
 */
export const useCallRecordTagMutation = ({
  channelId,
  threadCallId,
  threadId,
  callResult,
  locationId,
  entityCreatedAt,
  startTime,
  entityType,
  onApplySuccess,
  onApplyError,
  onDismissOptimisticSuccess,
  onDismissError,
  dismissDelay = DEFAULT_DELAY,
}: UseVoiceTagMutationArgs) => {
  const unifiedThreadUpdater = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();
  const { isCallRecordItem } = SMSDataSharedUtils.threadItemsUtils;
  const isVoicemail = callResult === CallResult.CALL_RESULT_VOICEMAIL;
  const isRobocall = entityType === EntityType.ENTITY_TYPE_ROBOCALL;

  // V4 tag mutations for robocall
  const v4ApplyTag = SMSDataV4.Mutations.useApplyTagMutation();
  const v4DismissTagWithUndo = SMSDataV4.Hooks.useDismissTagWithUndo({
    onUndoAvailable: onDismissOptimisticSuccess,
    onDismissError,
    delay: dismissDelay,
  });

  const userID = getUser()?.userID;

  const mutationContextRef = useRef<MutationContext>({
    updatedThreadItemTagIds: [],
  });

  // const queryClient = useQueryClient();

  /**
   * Converts a v1 Tag schema object to an InboxTag for unified thread compatibility.
   *
   * @param tag - The Tag object from v1 schema
   * @param entityId - The entity identifier (channelId for call records)
   * @returns InboxTag object compatible with unified thread system
   */
  const getInboxVoiceTagFromV1Tag = (tag: Tag): InboxTag => {
    // Determine entity type: robocall takes precedence, then voicemail, then call
    const resolvedEntityType = isRobocall
      ? EntityType.ENTITY_TYPE_ROBOCALL
      : isVoicemail
      ? EntityType.ENTITY_TYPE_VOICEMAIL
      : EntityType.ENTITY_TYPE_CALL;

    return {
      entityId: channelId,
      tagId: tag.id,
      entityType: resolvedEntityType,
      entityCreatedAt,
      appliedBy: userID || '',
    };
  };

  // Apply Tag

  /**
   * Updates both unified thread and thread item caches when applying a tag to a voice item.
   * Handles optimistic and non-optimistic updates for thread-level uniqueTags and call record tags.
   *
   * @param tag - The tag to apply to the voice item
   * @param isOptimistic - Whether this is an optimistic update (true) or server confirmation (false)
   */
  const addTagToUnifiedThreadAndThreadItem = useCallback(
    async (tag: Tag, isOptimistic: boolean) => {
      if (threadId && locationId) {
        // update unifiedThread
        const unifiedThreadResult = await unifiedThreadUpdater.upsertUnifiedThread({
          matchValues: {
            threadId,
            locationId,
          },
          updater: async (thread) => {
            if (!thread) return thread;

            const existingUniqueTags = thread.uniqueTags || [];
            const voiceTag = getInboxVoiceTagFromV1Tag(tag);
            const newTags = SMSDataSharedUtils.dedupeInboxTagsArray([...existingUniqueTags, voiceTag]);
            return {
              ...thread,
              uniqueTags: newTags,
              items:
                thread.items?.map((threadItem) => {
                  if (
                    !threadItem ||
                    !isCallRecordItem(threadItem) ||
                    threadItem.callRecord.threadCallId !== threadCallId
                  )
                    return threadItem;
                  return {
                    ...threadItem,
                    callRecord: {
                      ...threadItem.callRecord,
                      tags: SMSDataSharedUtils.dedupeInboxTagsArray([...(threadItem.callRecord.tags || []), voiceTag]),
                    },
                  };
                }) || [],
            };
          },
          isOptimistic,
        });

        // update unifiedThreadItem
        const unifiedThreadItemResult = await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadId,
            locationId,
            threadItemId: threadCallId,
          },
          updater: async (threadItem) => {
            if (!threadItem || !isCallRecordItem(threadItem)) return threadItem;

            const existingTags = threadItem.callRecord.tags || [];
            const voiceTag = getInboxVoiceTagFromV1Tag(tag);
            const newTags = SMSDataSharedUtils.dedupeInboxTagsArray([...existingTags, voiceTag]);
            mutationContextRef.current.updatedThreadItemTagIds = newTags.map((t) => t.tagId);
            return {
              ...threadItem,
              callRecord: {
                ...threadItem.callRecord,
                tags: newTags,
              },
            };
          },
          isOptimistic,
        });
        mutationContextRef.current.addTagDidOptimisticUpdate = isOptimistic;
        mutationContextRef.current.addTagRevertOptimisticUpdate = () => {
          unifiedThreadItemResult.revertCacheUpdate();
          unifiedThreadResult.revertCacheUpdate();
        };
      }
    },
    [channelId, threadId, locationId, threadCallId]
  );

  /**
   * Performs optimistic cache updates when applying a tag to a voice thread item.
   * Updates both listUnifiedThread and getUnifiedThread caches immediately.
   *
   * @param args - Object containing the tag to apply
   * @param args.tag - The tag to apply optimistically
   */
  const optimisticApplyTag = useCallback(async ({ tag }: tagMutationOptimisticFnArgs) => {
    addTagToUnifiedThreadAndThreadItem(tag, true);
  }, []);

  /**
   * Handles successful tag application by updating caches if optimistic update didn't occur.
   * Called after successful API response to ensure cache consistency.
   *
   * @param tag - The tag that was successfully applied
   */
  const onTagApplySuccess = useCallback(
    (tag: Tag) => {
      if (!mutationContextRef.current.addTagDidOptimisticUpdate) {
        addTagToUnifiedThreadAndThreadItem(tag, false);
      }

      onApplySuccess?.(tag);

      if (!!mutationContextRef.current.updatedThreadItemTagIds.length) {
        if (isVoicemail) {
          // reset voicemail insight panel data
          if (GlobalPanel.isOpen('voicemail-details')) {
            // reopening the voicemail forces refetch
            emitOpenVoicemailInsightPanel({ voicemailId: channelId, locationId: locationId || '' });
          }
        } else {
          // reset call insights panel data
          emitCallTagsExternalSync({
            channelId,
            tagIds: mutationContextRef.current.updatedThreadItemTagIds,
          });
        }
      }

      mutationContextRef.current.addTagDidOptimisticUpdate = false;
      mutationContextRef.current.updatedThreadItemTagIds = [];
    },
    [channelId, locationId, addTagToUnifiedThreadAndThreadItem, onApplySuccess, isVoicemail]
  );

  /**
   * Handles tag application errors by reverting optimistic updates.
   * Restores previous cache state and triggers error callback.
   *
   * @param error - The error that occurred during tag application
   * @param tag - The tag that failed to apply
   */
  const onTagApplyError = useCallback(
    (error: any, tag: Tag) => {
      mutationContextRef.current.addTagRevertOptimisticUpdate?.();
      onApplyError?.(error, tag);
    },
    [onApplyError]
  );

  // Dismiss Tag

  /**
   * Updates unified thread item cache when removing a tag from a voice item.
   * Only updates getUnifiedThread cache, not thread-level uniqueTags.
   *
   * @param tag - The tag to remove from the voice item
   * @param isOptimistic - Whether this is an optimistic update (true) or server confirmation (false)
   */
  const removeTagFromUnifiedThreadItem = useCallback(
    async (tag: Tag, isOptimistic: boolean) => {
      if (threadId && locationId) {
        // update unifiedThreadItem
        const unifiedThreadItemResult = await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadId,
            locationId,
            threadItemId: threadCallId,
          },
          updater: async (threadItem) => {
            if (!threadItem || !isCallRecordItem(threadItem)) return threadItem;
            mutationContextRef.current.updatedThreadItemTagIds =
              threadItem.callRecord.tags?.filter((t) => t.tagId !== tag.id).map((t) => t.tagId) || [];
            return {
              ...threadItem,
              callRecord: {
                ...threadItem.callRecord,
                tags: threadItem.callRecord.tags?.filter((threadItemTag) => threadItemTag.tagId !== tag.id) || [],
              },
            };
          },
          isOptimistic,
        });

        mutationContextRef.current.dismissTagDidOptimisticUpdate = isOptimistic;
        mutationContextRef.current.dismissTagRevertOptimisticUpdate = unifiedThreadItemResult.revertCacheUpdate;
      }
    },
    [channelId, threadId, locationId, threadCallId]
  );

  /**
   * Performs optimistic cache updates when dismissing a tag from a voice thread item.
   * Updates only getUnifiedThread cache immediately (not listUnifiedThread).
   *
   * @param args - Object containing the tag to dismiss
   * @param args.tag - The tag to dismiss optimistically
   */
  const optimisticRemoveTag = useCallback(({ tag }: tagMutationOptimisticFnArgs) => {
    removeTagFromUnifiedThreadItem(tag, true);
  }, []);

  /**
   * Handles successful tag dismissal by updating caches if optimistic update didn't occur.
   * Also updates thread-level uniqueTags to remove the tag reference.
   *
   * @param tag - The tag that was successfully dismissed
   */
  const onTagDismissSuccess = useCallback(
    async (tag: Tag) => {
      if (!mutationContextRef.current.dismissTagDidOptimisticUpdate) {
        removeTagFromUnifiedThreadItem(tag, false);
      }

      // unifiedThread update is taken care by INBOX_THREAD_ITEM_TAG ws event

      if (isVoicemail) {
        // reset voicemail insight panel data
        if (GlobalPanel.isOpen('voicemail-details')) {
          // reopening the voicemail forces refetch
          emitOpenVoicemailInsightPanel({ voicemailId: channelId, locationId: locationId || '' });
        }
      } else {
        // reset call insight panel data
        emitCallTagsExternalSync({
          channelId,
          tagIds: mutationContextRef.current.updatedThreadItemTagIds ?? [],
        });
      }

      mutationContextRef.current.dismissTagDidOptimisticUpdate = false;
      mutationContextRef.current.updatedThreadItemTagIds = [];
    },
    [channelId, isVoicemail, locationId]
  );

  /**
   * Handles tag dismissal errors by reverting optimistic updates.
   * Restores previous cache state and triggers error callback.
   *
   * @param error - The error that occurred during tag dismissal
   * @param tag - The tag that failed to dismiss
   */
  const onTagDismissError = useCallback(() => {
    mutationContextRef.current.dismissTagRevertOptimisticUpdate?.();
    onDismissError?.();
  }, [onDismissError]);

  /**
   * Applies a tag to a voice thread item with optimistic updates.
   * Updates cache immediately, then calls API, then confirms or reverts based on result.
   *
   * @param tag - The tag to apply to the voice thread item
   */
  const applyTag = useCallback(
    async (tag: Tag) => {
      // Robocall uses V4 tag mutations - cache updates handled by V4 mutation hooks
      if (isRobocall) {
        await v4ApplyTag.mutateAsync({
          tagId: tag.id,
          id: channelId,
          createdAt: entityCreatedAt,
          locationId: locationId || '',
          threadId: threadId || '',
          entityType: EntityType.ENTITY_TYPE_ROBOCALL,
        });
        return;
      }

      try {
        optimisticApplyTag({ tag });
        if (isVoicemail) {
          await VoicemailApi.tagVoicemail({ voicemailMessageId: channelId, tagIds: [tag.id] });
        } else {
          await PhoneCallsApi.applyCallRecordTags({
            channelId,
            tagIds: [tag.id],
            callRecordLocationId: locationId,
            startTime,
          });
        }
        onTagApplySuccess(tag);
      } catch (error) {
        onTagApplyError(error, tag);
      }
    },
    [channelId, locationId, startTime, isVoicemail, isRobocall, channelId, entityCreatedAt, threadId, v4ApplyTag]
  );

  /**
   * Dismisses a tag from a voice thread item with undo functionality.
   * Updates cache immediately, waits for delay period, then calls API unless undone.
   *
   * @param tag - The tag to dismiss from the voice thread item
   */
  const dismissTag = useCallback(
    (tag: Tag, appliedBy: string) => {
      // Robocall uses V4 dismiss tag with undo hook - handles optimistic updates and undo internally
      if (isRobocall) {
        v4DismissTagWithUndo({
          tagId: tag.id,
          id: channelId,
          locationId: locationId || '',
          threadId: threadId || '',
          originalAppliedBy: appliedBy,
          smsCreatedAt: entityCreatedAt,
        });
        return;
      }

      optimisticRemoveTag({ tag });

      const timeout = setTimeout(async () => {
        try {
          if (isVoicemail) {
            await VoicemailApi.untagVoicemail({ voicemailMessageId: channelId, tagIds: [tag.id] });
          } else {
            await PhoneCallsApi.removeCallRecordTags({
              channelId,
              tagIds: [tag.id],
              callRecordLocationId: locationId,
              startTime,
            });
          }
          onTagDismissSuccess(tag);
        } catch (error) {
          onTagDismissError();
        }
      }, dismissDelay + 1000);

      onDismissOptimisticSuccess?.(() => {
        clearTimeout(timeout);
        mutationContextRef.current.dismissTagRevertOptimisticUpdate?.();
      }, dismissDelay);
    },
    [
      channelId,
      locationId,
      startTime,
      dismissDelay,
      isVoicemail,
      isRobocall,
      channelId,
      threadId,
      v4DismissTagWithUndo,
      userID,
      entityCreatedAt,
    ]
  );

  return { applyTag, dismissTag };
};
